import { useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { LinkedinIcon, GithubIcon, InstagramIcon, ArrowRight, CheckCircle2 } from "lucide-react";
import { useLocation, useNavigate } from "react-router";

/* ─────────────── data ─────────────── */

const footerLinks = {
  Navigation: [
    { label: "Accueil", href: "/" },
    { label: "Services", href: "/services" },
    { label: "Blog", href: "/blog" },
    { label: "Portfolio", href: "/portfolio" },
    { label: "Contact", href: "/contact" },
  ],
  Services: [
    { label: "Développement Web", href: "#services" },
    { label: "UI/UX Design", href: "#services" },
    { label: "Performance & SEO", href: "#services" },
    { label: "Stratégie Digitale", href: "#services" },
    { label: "Intégration Figma to Code", href: "#services" },
    { label: "Maintenance & Support", href: "#services" },
  ],
};

const socials = [
  { icon: LinkedinIcon, href: "https://www.linkedin.com/in/serge-tovmassian/", label: "LinkedIn" },
  { icon: GithubIcon, href: "https://github.com/Serge-Tovmassian", label: "GitHub" },
  { icon: InstagramIcon, href: "https://www.instagram.com/awaken.code.dev/", label: "Instagram" },
];

const NEWSLETTER_STATUS = {
  IDLE: "idle",
  LOADING: "loading",
  SUCCESS: "success",
  ERROR: "error",
} as const;


type NewsletterStatus = (typeof NEWSLETTER_STATUS)[keyof typeof NEWSLETTER_STATUS];

function getColHeadingStyle(): React.CSSProperties {
  return {
    fontFamily: "'Syne', sans-serif",
    fontWeight: 600,
    fontSize: "0.8rem",
    color: "rgba(255,255,255,0.5)",
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    marginBottom: "1.25rem",
  };
}

/* ─────────────── component ─────────────── */

export function Footer() {
  const [email, setEmail] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [focused, setFocused] = useState(false);
  const [newsletterStatus, setNewsletterStatus] = useState<NewsletterStatus>(NEWSLETTER_STATUS.IDLE);
  const [newsletterError, setNewsletterError] = useState("");
  const [newsletterSuccessMessage, setNewsletterSuccessMessage] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const colHeading = getColHeadingStyle();

  const isLoading = newsletterStatus === NEWSLETTER_STATUS.LOADING;
  const isSuccess = newsletterStatus === NEWSLETTER_STATUS.SUCCESS;
  const isError = newsletterStatus === NEWSLETTER_STATUS.ERROR;

  const handleSubscribe = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!email.trim()) {
      return;
    }

    setNewsletterStatus(NEWSLETTER_STATUS.LOADING);
    setNewsletterError("");
    setNewsletterSuccessMessage("");

    try {
      const subscribeResponse = await fetch("/api/subscribe", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
          honeypot,
        }),
      });

      const rawSubscribeResponse = await subscribeResponse.text();
      let subscribePayload: { message?: string; error?: string; status?: string } | null = null;

      try {
        subscribePayload = JSON.parse(rawSubscribeResponse) as {
          message?: string;
          error?: string;
          status?: string;
        };
      } catch {
        subscribePayload = null;
      }

      if (!subscribeResponse.ok) {
        throw new Error(
          subscribePayload?.message ??
            subscribePayload?.error ??
            "Service newsletter temporairement indisponible.",
        );
      }

      setNewsletterStatus(NEWSLETTER_STATUS.SUCCESS);
      setNewsletterSuccessMessage(
        subscribePayload?.status === "already_active"
          ? "Adresse déjà inscrite // Email de bienvenue non renvoyé"
          : subscribePayload?.message ?? "Inscription confirmée.",
      );
      setEmail("");
      setHoneypot("");
    } catch (subscriptionError) {
      const resolvedErrorMessage =
        subscriptionError instanceof Error
          ? subscriptionError.message
          : "Erreur réseau, réessayez dans quelques secondes.";

      setNewsletterStatus(NEWSLETTER_STATUS.ERROR);
      setNewsletterError(resolvedErrorMessage);
    }
  };

  return (
    <footer
      className="relative pt-20 pb-8 px-6 overflow-hidden"
      style={{ fontFamily: "'Space Grotesk', sans-serif" }}
    >
      {/* Top border line (animated from center) */}
      <motion.div
        initial={{ scaleX: 0, opacity: 0.35 }}
        whileInView={{ scaleX: 1, opacity: 1 }}
        viewport={{ once: true, margin: "-20% 0px -20% 0px" }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-px pointer-events-none"
        style={{
          transformOrigin: "center",
          background: "linear-gradient(90deg, transparent, rgba(0,255,65,0.26), transparent)",
        }}
      />

      <div className="max-w-7xl mx-auto">

        {/* ══════════════════════════════════════════
            MAIN COLUMNS GRID
            Desktop : Brand(2fr) | Nav(1fr) | Services(1fr) | Newsletter(2.2fr)
            Tablet  : 2 columns
            Mobile  : 1 column
        ══════════════════════════════════════════ */}
        <style>{`
          @media (min-width: 1024px) {
            .footer-grid {
              grid-template-columns: 2fr 1fr 1fr 2.2fr;
            }
          }
          @media (min-width: 768px) and (max-width: 1023px) {
            .footer-grid {
              grid-template-columns: 1fr 1fr;
            }
          }
        `}</style>

        {/* Real grid (motion only wraps the fade-in, grid is here) */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="footer-grid grid grid-cols-1 md:grid-cols-2 mb-16"
          style={{
            columnGap: "clamp(2rem, 4vw, 4.5rem)",
            rowGap: "3rem",
          }}
        >

          {/* ── Column 1 — Brand ── */}
          <div>
            {/* Logo */}
            <div className="flex items-center gap-2 mb-5">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center overflow-hidden"
                style={{
                  background: "linear-gradient(135deg, #00ff41, #00cc33)",
                  boxShadow: "0 0 16px rgba(0,255,65,0.3)",
                }}
              >
                <img
                  src="/logo.webp"
                  alt="Logo Awaken Code"
                  width={24}
                  height={24}
                  loading="lazy"
                  decoding="async"
                  style={{ width: "24px", height: "24px", objectFit: "contain" }}
                />
              </div>
              <span
                style={{
                  fontFamily: "'Syne', sans-serif",
                  fontWeight: 700,
                  fontSize: "1.15rem",
                  color: "#fff",
                  letterSpacing: "-0.02em",
                }}
              >
                Awaken<span style={{ color: "#00ff41" }}>Code</span>
              </span>
            </div>

            {/* Description */}
            <p
              style={{
                color: "#fffc",
                opacity: 0.7,
                fontSize: "0.875rem",
                lineHeight: 1.8,
                maxWidth: "300px",
                marginBottom: "1.5rem",
              }}
            >
              Je construis des sites qui marquent les esprits.
            </p>

            {/* Socials */}
            <div className="flex items-center gap-3">
              {socials.map((social) => {
                const Icon = social.icon;
                return (
                  <a
                    key={social.label}
                    href={social.href}
                    aria-label={social.label}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 rounded-lg flex items-center justify-center duration-200 hover:bg-[#00ff411a] hover:border-[#00ff4140] hover:text-[#00ff41]"
                    onMouseEnter={(e) => {
                      const el = e.currentTarget as HTMLElement;
                      el.style.color = "#00FF41";
                      el.style.opacity = "1";
                      el.style.background = "rgba(0,255,65,0.1)";
                      el.style.borderColor = "rgba(0,255,65,0.25)";
                    }}
                    onMouseLeave={(e) => {
                      const el = e.currentTarget as HTMLElement;
                      el.style.color = "rgba(255,255,255,0.7)";
                      el.style.opacity = "0.7";
                      el.style.background = "rgba(255,255,255,0.04)";
                      el.style.borderColor = "rgba(255,255,255,0.08)";
                    }}
                    style={{
                      background: "rgba(255,255,255,0.04)",
                      border: "1px solid rgba(255,255,255,0.08)",
                      color: "rgba(255,255,255,0.7)",
                      opacity: 0.7,
                      transition: "color 0.2s ease, opacity 0.2s ease, background 0.2s ease, border-color 0.2s ease",
                    }}
                  >
                    <Icon size={15} />
                  </a>
                );
              })}
            </div>
          </div>

          {/* ── Columns 2 & 3 — Nav links ── */}
          {Object.entries(footerLinks).map(([category, links]) => (
            <div key={category}>
              <p style={colHeading}>{category}</p>
              <ul className="flex flex-col gap-2.5">
                {links.map((link) => (
                  <li key={link.label}>
                    <a
                      href={link.href}
                      onClick={(e) => {
                        e.preventDefault();
                        if (link.href.startsWith("/")) {
                          navigate(link.href);
                          return;
                        }

                        if (link.href === "/contact") {
                          if (location.pathname === "/") {
                            const el = document.querySelector("contact");
                            if (el) {
                              el.scrollIntoView({ behavior: "smooth" });
                              navigate({ pathname: "/", hash: "contact" }, { replace: true });
                            }
                            return;
                          }
                          navigate({ pathname: "/", hash: "contact" });
                          return;
                        }

                        const el = document.querySelector(link.href);
                        if (el) el.scrollIntoView({ behavior: "smooth" });
                      }}
                      className="inline-block text-[rgba(255,255,255,0.8)] opacity-70 transition-all duration-200 hover:!text-[#00ff41] hover:opacity-100"
                      style={{
                        fontSize: "0.875rem",
                        textDecoration: "none",
                      }}
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* ── Column 4 — Newsletter ── */}
          <div>
            <p style={colHeading}>Newsletter</p>

            {/* Title */}
            <p
              style={{
                fontFamily: "'Syne', sans-serif",
                fontWeight: 700,
                fontSize: "1.05rem",
                color: "#ffffff",
                letterSpacing: "-0.02em",
                lineHeight: 1.2,
                marginBottom: "0.65rem",
              }}
            >
              Restez informé
            </p>

            {/* Description */}
            <p
              style={{
                color: "#fffc",
                opacity: 0.7,
                fontSize: "0.83rem",
                lineHeight: 1.75,
                marginBottom: "1.25rem",
              }}
            >
              Recevez mes dernières réalisations et actualités web directement
              dans votre boîte mail.
            </p>

            <form onSubmit={handleSubscribe} className="flex flex-col gap-2.5">
              <input
                type="email"
                required
                placeholder="Votre adresse email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                disabled={isLoading}
                style={{
                  width: "100%",
                  background: "rgba(255,255,255,0.04)",
                  border: `1px solid ${
                    focused
                      ? "rgba(0,255,65,0.45)"
                      : "rgba(255,255,255,0.09)"
                  }`,
                  boxShadow: focused
                    ? "0 0 0 3px rgba(0,255,65,0.07), inset 0 1px 0 rgba(0,255,65,0.04)"
                    : "none",
                  borderRadius: "9px",
                  padding: "0.72rem 1rem",
                  color: "#fff",
                  fontSize: "0.85rem",
                  fontFamily: "'Space Grotesk', sans-serif",
                  outline: "none",
                  transition: "border-color 0.22s, box-shadow 0.22s, opacity 0.22s",
                  caretColor: "#00ff41",
                  opacity: isLoading ? 0.7 : 1,
                }}
              />
              <input
                type="text"
                name="honeypot"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="hidden"
              />
              <button
                type="submit"
                disabled={isLoading}
                className="group flex items-center justify-center gap-2 w-full py-3 rounded-xl transition-all duration-300 hover:-translate-y-px disabled:opacity-70 disabled:cursor-not-allowed"
                style={{
                  background: "linear-gradient(135deg, #00ff41, #00cc33)",
                  color: "#000",
                  fontWeight: 700,
                  fontSize: "0.88rem",
                  border: "none",
                  cursor: "pointer",
                  fontFamily: "'Space Grotesk', sans-serif",
                  letterSpacing: "0.02em",
                  boxShadow: "0 0 22px rgba(0,255,65,0.28), 0 4px 14px rgba(0,0,0,0.35)",
                }}
              >
                {isLoading ? "Transmission..." : "S'abonner"}
                <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
              </button>
            </form>

            <AnimatePresence mode="wait">
              {isSuccess && (
                <motion.div
                  key="newsletter-success"
                  initial={{ opacity: 0, y: 6, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -4, scale: 0.98 }}
                  transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
                  className="flex items-center gap-2 px-4 py-3 rounded-xl mt-2"
                  style={{
                    background: "rgba(0,255,65,0.07)",
                    border: "1px solid rgba(0,255,65,0.2)",
                  }}
                >
                  <CheckCircle2 size={15} color="#00ff41" style={{ flexShrink: 0 }} />
                  <span
                    style={{
                      color: "#00ff41",
                      fontSize: "0.82rem",
                      fontWeight: 600,
                      fontFamily: "'Share Tech Mono', monospace",
                    }}
                  >
                    {newsletterSuccessMessage || "TRANSMISSION ACCEPTED // INSCRIPTION CONFIRMÉE"}
                  </span>
                </motion.div>
              )}

              {isError && (
                <motion.div
                  key="newsletter-error"
                  initial={{ opacity: 0, y: 6, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -4, scale: 0.98 }}
                  transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
                  className="px-4 py-3 rounded-xl mt-2"
                  style={{
                    background: "rgba(255,60,60,0.08)",
                    border: "1px solid rgba(255,60,60,0.22)",
                    color: "rgba(255,180,180,0.95)",
                    fontSize: "0.8rem",
                    lineHeight: 1.55,
                  }}
                >
                  {newsletterError || "Erreur de connexion, réessayez."}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Privacy note */}
            <p
              style={{
                color: "#fffc",
                opacity: 0.7,
                fontSize: "0.68rem",
                marginTop: "0.75rem",
                letterSpacing: "0.02em",
                lineHeight: 1.6,
              }}
            >
              Pas de spam. Désinscription à tout moment. L'email de confirmation peut arriver dans le dossier Spam.
            </p>
          </div>

        </motion.div>

        {/* ── Bottom bar ── */}
        <div
          className="flex flex-col md:flex-row items-center justify-between gap-4 pt-8"
          style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}
        >
          <p
            style={{
              color: "#fffc",
              opacity: 0.7,
              fontSize: "0.8rem",
              letterSpacing: "0.02em",
            }}
          >
            Copyright© 2026 Awaken Code, tous droits réservés.
          </p>
          <nav className="flex flex-wrap justify-center gap-x-5 gap-y-2" aria-label="Informations légales">
            {[
              { label: "Mentions légales", href: "/mentions-legales" },
              { label: "CGV", href: "/cgv" },
              { label: "Confidentialité", href: "/confidentialite" },
            ].map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(link.href);
                }}
                className="text-[rgba(255,255,255,0.8)] opacity-70 transition-all duration-200 hover:!text-[#00ff41] hover:opacity-100"
                style={{ fontSize: "0.8rem", textDecoration: "none" }}
              >
                {link.label}
              </a>
            ))}
          </nav>

        </div>

      </div>
    </footer>
  );
}
